/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { TextField, Tooltip, InputAdornment, IconButton } from "@material-ui/core";
import { SET_VALUE_CALLBACK_DEBOUNCE_RATE } from "../../utility/constants";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
/**
 * This component is used to store the value of a field until the field losses focus. Then the value is returned to the parent component.
 * This cuts down dramatically on the amount of total form rerenders needed.
 *
 * @param {(String, Any) => void} setConfigValue Callback method to set the value entered in the text field to whatever given field in the chat config.
 * @param {(String, Any) => void} onBlurEvent Optional callback method triggered when the field is no longer selected. Passes (valueName, value) to function.
 * @param {String} startingValue The current value of the field in the chat config object.
 * @param {String} valueName The name of the field in the chat config object.
 * @param {String} label The label to be shown on the page.
 * @param {String} type The type of input to accept in the text field.
 * @param {(Boolean) => void} setEdited A callback method to set the form as edited. Called only the first change and if the form is not already marked as edited.
 * @param {Boolean} formEdited A boolean indicating whether or not the form has already been edited.
 * @param {Boolean} required A boolean indicating whether or not the field is required.
 * @param {String} errorMessage The error message to be shown when field is empty if it is required.
 * @param {String} toolTipMessage The message to be shown in the tooltip when the field is hovered over.
 * @param {Boolean} disabled A boolean indicating whether or not the field is disabled.
 * @param {Boolean} enableDebounce A boolean indicating whther the data should be debounced back to the parent component when a user momentarily stops typing.
 * @param {Boolean} placeholder A short hint displayed in the input before the user enters a value.
 * @param {Boolean} autoFocus Set focus on input
 * @param {Object} inputProps these props will be set to Input field.
 * @param {(String) => Boolean | String} errorCheck Callback method that returns true if an error is detected. Can also return a string. In the case of a errorMessage not being provided, the returned string will be displayed.
 */
const FormTextField = ({
	setConfigValue,
	onBlurEvent,
	startingValue,
	valueName,
	label,
	type,
	setEdited,
	formEdited,
	required,
	errorMessage,
	toolTipMessage,
	disabled,
	styleObject,
	multiline,
	maxLineRows,
	errorCheck,
	enableDebounce,
	placeholder,
	maskValue,
	inputProps = {},
	autoFocus = false,
	adornment,
	helperText,
	fullWidth,
}) => {
	const [initialValue, setInitialValue] = React.useState(startingValue);
	const [value, setValue] = React.useState(startingValue);
	const [configValueCallBackDebounce, setConfigValueCallBackDebounce] = React.useState(undefined);
	const [maskRealValue, setMaskRealValue] = React.useState(startingValue && maskValue ? true : false);

	let error = false;
	if (errorCheck && errorCheck(value)) {
		error = true;
	} else if (required) {
		if (type === "number" && isNaN(value)) {
			error = true;
		} else if (typeof value === "string" && value.trim().length === 0) {
			error = true;
		}
	}

	//This check is to make sure we populate value with the newest value being
	// passed in if the config has changed.
	if (initialValue !== startingValue) {
		setValue(startingValue);
		setInitialValue(startingValue);
	}
	const onBlur = (event) => {
		const edited = startingValue !== event.target.value;
		if (edited) {
			setConfigValue(valueName, value);
		}
		if (onBlurEvent) onBlurEvent(valueName, value);
	};
	React.useEffect(() => {
		setValue(startingValue);
		setInitialValue(startingValue);
	}, [startingValue]);

	const onChange = (event) => {
		if (enableDebounce) {
			if (configValueCallBackDebounce) clearTimeout(configValueCallBackDebounce);
			const val = event.target.value;
			setConfigValueCallBackDebounce(
				setTimeout(() => {
					setConfigValue(valueName, val);
					setConfigValueCallBackDebounce(undefined);
				}, SET_VALUE_CALLBACK_DEBOUNCE_RATE),
			);
		}
		if (!formEdited) setEdited(true);
		setValue(event.target.value);
	};
	const style = styleObject || { margin: "25px", minWidth: "40%" };
	//If no error message is provided and an error has been detected we display the value returned by the errorCheck method.
	//Otherwise if the helperText prop has been used we display that.
	const helperTextElement = error ? errorMessage || (errorCheck ? errorCheck(value) : "") : helperText || "";
	let reactInputProps = { ...inputProps };
	const materialUIInputProps = {};
	let inputValue = value;

	if (toolTipMessage) {
		materialUIInputProps["startAdornment"] = (
			<InputAdornment position="start">
				<Tooltip arrow interactive placement={"bottom-start"} title={toolTipMessage}>
					<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
				</Tooltip>
			</InputAdornment>
		);
	}
	if (adornment) {
		materialUIInputProps["endAdornment"] = <InputAdornment position="end">{adornment}</InputAdornment>;
	} else if (maskValue) {
		if (maskRealValue) {
			inputValue = "";
			let x = 0;
			while (x < value.length) {
				inputValue += "*";
				x++;
			}
		}
		materialUIInputProps["endAdornment"] = (
			<InputAdornment position="end">
				<IconButton onClick={() => setMaskRealValue(!maskRealValue)}>
					{maskRealValue ? <VisibilityIcon fontSize={"small"} /> : <VisibilityOffIcon fontSize={"small"} />}
				</IconButton>
			</InputAdornment>
		);
	}

	if (type === "color") {
		materialUIInputProps["disableUnderline"] = true;
		reactInputProps.style = reactInputProps.style || { height: "30px" };
	}
	return (
		<>
			<TextField
				autoComplete="new-password" // prevents showing auto fill dropdown in Chrome
				autoFocus={autoFocus}
				label={label}
				style={style}
				type={type}
				error={error}
				disabled={disabled || maskRealValue}
				helperText={helperTextElement}
				value={inputValue}
				inputProps={reactInputProps}
				InputProps={materialUIInputProps}
				onChange={onChange}
				onBlur={onBlur}
				multiline={multiline}
				maxRows={maxLineRows || 1}
				placeholder={placeholder}
				fullWidth={fullWidth}
			/>
		</>
	);
};

export default FormTextField;
